/**
 * Every piece of user-visible wording in the application, in one place.
 *
 * Nothing outside this file should contain a literal string that a person will
 * read. Components import a label rather than typing one, so a wording change
 * happens once, and so the same concept is never called two different things on
 * two different screens.
 *
 * Grouped by where the text appears, not by what it says — when you are editing
 * a screen, the group you want has the screen's name on it.
 */
export const labels = {
  app: {
    name: 'SriChaitra',
    tagline: 'Seafood trading',
    shortName: 'SCSF'
  },

  login: {
    heading: 'Sign in',
    subheading: 'Use your SriChaitra account to continue',
    userId: 'User ID',
    userIdPlaceholder: 'e.g. rkumar',
    password: 'Password',
    passwordPlaceholder: 'Your password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    sessionExpired: 'Your session ended. Please sign in again.',
    showPassword: 'Show password',
    hidePassword: 'Hide password'
  },

  dashboard: {
    heading: 'Business dashboard',
    description: 'Where the business stands right now',
    welcome: 'Welcome back',
    signedInAs: 'Signed in as',
    role: 'Role',
    lastLogin: 'Last sign-in',
    tilesHeading: 'Your access',
    noTiles: 'No sections have been granted to this account yet.',

    // The headline figures. Each caption says what the number actually counts,
    // because "spending" means several different things in a trading business
    // and a dashboard that does not say which is being shown invites the wrong
    // decision.
    sales: 'Overall sales',
    salesHint: 'Total value of every sale recorded',

    stockSpending: 'Stock spending',
    stockSpendingHint: 'What the stock that has actually sold cost to buy',

    miscSpending: 'Misc spending',
    miscSpendingHint: 'Every expense booked, whether paid yet or not',

    investment: 'Investment',
    investmentHint: 'Money put into the business',
    investmentSelf: 'Own',
    investmentOther: 'Borrowed',

    profit: 'Profit',
    loss: 'Loss',
    profitHint: 'Sales less the cost of stock sold and expenses',

    positionHeading: 'Position',
    payable: 'Payable',
    payableHint: 'Still owed to sellers and on expenses',
    receivable: 'Receivable',
    receivableHint: 'Cost still to earn back from unsold stock',
    cashInHand: 'Cash in hand',
    cashInHandHint: 'Money taken in, less everything paid out',

    // "Built-in" is the load-bearing word. The server's rule is the admin tile
    // *plus* roles.is_system, so a custom role granted the admin tile still does
    // not qualify — without that word, someone would ask an administrator for a
    // permission that would not actually help.
    limitedNotice:
      'You are seeing the figures for your own records. The full business position is restricted to the built-in Super Admin role.',
    reload: 'Refresh'
  },

  profitLoss: {
    heading: 'Profit and loss',
    caption:
      'How sales became profit: the cost of the stock that sold, and the expenses booked, taken off the top. Unpaid bills count against profit — a debt run up is a cost incurred.',

    sales: 'Sales',
    costOfStockSold: 'Cost of stock sold',
    miscExpenses: 'Misc expenses',
    profit: 'Profit',
    loss: 'Loss',

    // The hero figure: the one answer the whole section exists to give.
    resultLabel: 'Result to date',
    heroCaption: 'Sales less the cost of stock sold and expenses booked',

    restricted:
      'Profit and loss is restricted to the built-in Super Admin role, so it is not shown here.',

    showTable: 'Show table',
    showChart: 'Show chart',
    columns: { item: 'Item', amount: 'Amount' }
  },

  trends: {
    heading: 'Trends',

    // The measure names. "Stock purchased" is deliberately not "Stock spending":
    // the tile above counts what the stock that has *sold* cost, while this
    // counts the value of lots bought in the month. They are different numbers
    // and giving them one name would make the dashboard contradict itself.
    sales: 'Sales',
    purchases: 'Stock purchased',
    misc: 'Misc expenses',

    monthlyCaption:
      'Value recorded in each month of the selected year. Stock purchased is the value of lots bought — not the cost of stock sold shown above.',

    year: 'Year',
    allYears: 'All years',
    other: 'Other',

    // Second level — the per-measure grouping, named for what it actually is.
    byGroup: {
      sales: 'by lot supplier',
      purchases: 'by supplier',
      misc: 'by expense'
    },

    drillHint: 'Select a bar to break it down',
    drillHintLevel2: 'Select a bar to list its transactions',
    backToOverview: 'All months',
    transactionsHeading: 'Transactions',
    otherMembers: (count) => `${count} smaller entries combined`,

    showTable: 'Show table',
    showChart: 'Show chart',
    tableCaption: 'The same figures as the chart, as text',

    noActivity: 'No activity recorded for this year yet.',
    noHistoryAccess: 'Trends need access to History, which this role does not have.',

    columns: {
      month: 'Month',
      name: 'Name',
      date: 'Date',
      counterparty: 'Counterparty',
      description: 'Description',
      recordedBy: 'Recorded by',
      count: 'Records',
      amount: 'Amount'
    }
  },

  nav: {
    dashboard: 'Dashboard',
    signOut: 'Sign out',
    menu: 'Menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    skipToContent: 'Skip to main content'
  },

  actions: {
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    retry: 'Try again',
    close: 'Close',
    dismiss: 'Dismiss'
  },

  startup: {
    failedHeading: 'The application could not start'
  },

  common: {
    loading: 'Loading…',
    required: 'Required',
    optional: 'Optional',
    never: 'Never',
    notFoundHeading: 'Page not found',
    notFoundBody: 'That page does not exist, or you do not have access to it.',
    backToDashboard: 'Back to dashboard'
  }
};
